import { memo, useEffect, useState } from 'react';
import {
  Trash2,
  Check,
  Bold,
  Strikethrough,
  Link,
  Link2Off,
  List,
  Type,
  ChevronDown,
  X,
} from 'lucide-react';

import {
  StickyNoteColor,
  StickyNoteData,
  STICKY_NOTE_COLOR_OPTIONS,
  DEFAULT_STICKY_NOTE_COLOR,
} from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';
import { cn } from '@/lib/utils';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

export type TextFormat = 'bold' | 'strikethrough' | 'link' | 'bullet';

const SWATCH_BUTTON_CLASS =
  'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border p-0 hover:bg-transparent [&_svg]:size-3';

const SWATCH_SLOT_CLASS =
  'flex size-7 shrink-0 items-center justify-center rounded-md p-0 hover:bg-muted';

const swatchDotClass = (active: boolean) =>
  cn(
    SWATCH_BUTTON_CLASS,
    'ring-2 ring-offset-0',
    active ? 'ring-primary' : 'ring-transparent',
  );

// Tailwind-aligned font sizes: Small→text-xs(12px), Medium→text-sm(14px),
// Large→text-lg(18px), Extra Large→text-2xl(24px), Huge→text-4xl(36px).
const FONT_SIZE_OPTIONS = [
  { label: 'Small', size: '0.75rem', lineHeight: '1rem' },
  { label: 'Medium', size: '0.875rem', lineHeight: '1.25rem' },
  { label: 'Large', size: '1.125rem', lineHeight: '1.75rem' },
  { label: 'Extra Large', size: '1.5rem', lineHeight: '2rem' },
  { label: 'Huge', size: '2.25rem', lineHeight: '2.5rem' },
] as const;

// Shown in the toolbar when nothing is selected. Matches the sticky note's base
// text size (text-xs / 12px).
const DEFAULT_FONT_SIZE_LABEL = FONT_SIZE_OPTIONS[0].label;

// Maps getComputedStyle px values back to size labels for the toolbar indicator.
const FONT_SIZE_PX_TO_LABEL: Record<string, string> = {
  '12px': 'Small',
  '14px': 'Medium',
  '18px': 'Large',
  '24px': 'Extra Large',
  '36px': 'Huge',
};

const FORMAT_BUTTONS: {
  format: TextFormat;
  icon: React.ReactNode;
  label: string;
}[] = [
  { format: 'bold', icon: <Bold className='size-3.5' />, label: 'Bold' },
  {
    format: 'strikethrough',
    icon: <Strikethrough className='size-3.5' />,
    label: 'Strikethrough',
  },
  { format: 'link', icon: <Link className='size-3.5' />, label: 'Link' },
  {
    format: 'bullet',
    icon: <List className='size-3.5' />,
    label: 'Bullet list',
  },
];

// Keeps focus on the ContentEditable when clicking toolbar buttons so that
// execCommand and selection-based operations work on the current selection.
const keepFocus = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

interface StickyNoteToolbarProps {
  nodeId: string;
  data: StickyNoteData;
  isEditing: boolean;
  onFormat: (format: TextFormat) => void;
  onFontSize: (size: string, lineHeight: string) => void;
  /** Called on mousedown of the font-size trigger to snapshot the selection
   *  before the Radix portal opens and potentially collapses it. */
  onSaveFontSizeSelection?: () => void;
  selectionFontSize?: string | null;
  isLinkInputOpen: boolean;
  existingLinkUrl?: string | null;
  onLinkApply: (url: string) => void;
  onLinkCancel: () => void;
  onLinkRemove: () => void;
}

const COLOR_KEYS = Object.keys(STICKY_NOTE_COLOR_OPTIONS) as StickyNoteColor[];

export const StickyNoteToolbar: React.FC<StickyNoteToolbarProps> = memo(
  ({
    nodeId,
    data,
    isEditing,
    onFormat,
    onFontSize,
    onSaveFontSizeSelection,
    selectionFontSize,
    isLinkInputOpen,
    existingLinkUrl,
    onLinkApply,
    onLinkCancel,
    onLinkRemove,
  }) => {
    const updateStickyNoteData = useNodeStore(
      (state) => state.updateStickyNoteData,
    );
    const onNodesEdgesDelete = useNodeStore(
      (state) => state.onNodesEdgesDelete,
    );

    const currentColor: StickyNoteColor =
      (data?.backgroundColor as StickyNoteColor) ?? DEFAULT_STICKY_NOTE_COLOR;
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const [isSizeOpen, setIsSizeOpen] = useState(false);
    const [linkInputValue, setLinkInputValue] = useState('');

    useEffect(() => {
      if (!isLinkInputOpen) {
        setLinkInputValue('');
      } else {
        setLinkInputValue(existingLinkUrl ?? '');
      }
    }, [isLinkInputOpen, existingLinkUrl]);

    const { bgColor: currentBgColor, borderColor: currentBorderColor } =
      STICKY_NOTE_COLOR_OPTIONS[currentColor] ??
      STICKY_NOTE_COLOR_OPTIONS[DEFAULT_STICKY_NOTE_COLOR];

    const handleConfirmDelete = () => {
      onNodesEdgesDelete({ nodeId });
      setIsDeleteConfirmOpen(false);
    };

    const sizeLabel =
      (selectionFontSize ? FONT_SIZE_PX_TO_LABEL[selectionFontSize] : null) ??
      DEFAULT_FONT_SIZE_LABEL;

    return (
      <>
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.stopPropagation();
            // When the link input is open, allow focus to move into the input.
            if (!isLinkInputOpen) e.preventDefault();
          }}
          className='nodrag nopan absolute -top-13 left-1/2 z-[2100] flex -translate-x-1/2 items-center gap-1 rounded-lg border border-border bg-card p-1 shadow-lg'
        >
          {isLinkInputOpen ? (
            <div className='flex items-center gap-1'>
              <Link className='size-3.5 shrink-0 text-muted-foreground' />
              <input
                type='url'
                aria-label='Link URL'
                placeholder='Paste link…'
                autoFocus
                value={linkInputValue}
                onChange={(e) => setLinkInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onLinkApply(linkInputValue);
                  } else if (e.key === 'Escape') {
                    onLinkCancel();
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className='h-6 w-44 bg-transparent px-1 text-xs outline-none placeholder:text-muted-foreground dark:text-white'
              />
              <Button
                variant='ghost'
                size='icon'
                aria-label='Apply link'
                className='size-6 shrink-0'
                onClick={() => onLinkApply(linkInputValue)}
              >
                <Check className='size-3.5 text-primary' />
              </Button>
              {existingLinkUrl != null && (
                <Button
                  variant='ghost'
                  size='icon'
                  aria-label='Remove link'
                  className='size-6 shrink-0'
                  onClick={onLinkRemove}
                >
                  <Link2Off className='size-3.5 text-destructive' />
                </Button>
              )}
              <Button
                variant='ghost'
                size='icon'
                aria-label='Cancel link'
                className='size-6 shrink-0'
                onClick={onLinkCancel}
              >
                <X className='size-3.5' />
              </Button>
            </div>
          ) : (
            <>
              {/* Color picker */}
              <Popover
                open={isColorPickerOpen}
                onOpenChange={setIsColorPickerOpen}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        aria-label={`Change sticky note color (${currentColor})`}
                        className={SWATCH_SLOT_CLASS}
                      >
                        <span
                          className={SWATCH_BUTTON_CLASS}
                          style={{
                            backgroundColor: currentBgColor,
                            borderColor: currentBorderColor,
                          }}
                        />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side='top'>
                    <p className='capitalize'>Change color</p>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent
                  side='top'
                  align='center'
                  sideOffset={8}
                  className='nodrag nopan w-auto p-1.5'
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div
                    className='flex items-center gap-1'
                    role='listbox'
                    aria-label='Sticky note colors'
                  >
                    {COLOR_KEYS.map((key) => {
                      const { bgColor, borderColor } =
                        STICKY_NOTE_COLOR_OPTIONS[key];
                      const active = currentColor === key;
                      return (
                        <Tooltip key={key}>
                          <TooltipTrigger asChild>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              role='option'
                              aria-selected={active}
                              aria-label={`Change sticky note color to ${key}`}
                              onClick={() => {
                                updateStickyNoteData(nodeId, {
                                  backgroundColor: key,
                                });
                                setIsColorPickerOpen(false);
                              }}
                              className={SWATCH_SLOT_CLASS}
                            >
                              <span
                                className={swatchDotClass(active)}
                                style={{
                                  backgroundColor: bgColor,
                                  borderColor: borderColor,
                                }}
                              >
                                {active && (
                                  <Check
                                    className='size-3 text-black/70'
                                    strokeWidth={3}
                                  />
                                )}
                              </span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side='top'>
                            <p className='capitalize'>{key}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>

              <div className='h-5 w-px bg-border' />

              {/* Font size dropdown — applies to selected text */}
              <Popover open={isSizeOpen} onOpenChange={setIsSizeOpen}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='ghost'
                        aria-label='Font size'
                        onMouseDown={(e) => {
                          keepFocus(e);
                          if (!isSizeOpen) onSaveFontSizeSelection?.();
                        }}
                        className={cn(
                          'flex h-7 items-center gap-0.5 rounded-md px-1.5 hover:bg-muted',
                          !isEditing && 'opacity-40',
                        )}
                      >
                        <Type className='size-3.5 text-foreground pr-1' />
                        {sizeLabel && (
                          <span className='text-[10px] font-medium text-foreground'>
                            {sizeLabel}
                          </span>
                        )}
                        <ChevronDown className='size-3 text-muted-foreground' />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side='top'>
                    <p>Font size (applies to whole line)</p>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent
                  side='top'
                  align='start'
                  sideOffset={8}
                  className='nodrag nopan w-48 p-1'
                  // Prevent Radix from moving focus to the popover on open and
                  // back to the trigger on close — keeps the ContentEditable
                  // focused so the selection (and the user's typing) is preserved.
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {FONT_SIZE_OPTIONS.map(({ label, size, lineHeight }) => (
                    <Button
                      key={label}
                      variant='ghost'
                      className='flex h-auto w-full items-center justify-start rounded px-2 py-1 text-left text-black dark:text-white'
                      style={{ fontSize: size, lineHeight }}
                      onClick={() => {
                        onFontSize(size, lineHeight);
                        setIsSizeOpen(false);
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </PopoverContent>
              </Popover>

              <div className='h-5 w-px bg-border' />

              {/* Text format buttons (visually dimmed when not in edit mode) */}
              {FORMAT_BUTTONS.map(({ format, icon, label }) => (
                <Tooltip key={format}>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label={label}
                      onMouseDown={keepFocus}
                      onClick={() => onFormat(format)}
                      className={cn(
                        'flex size-7 items-center justify-center rounded-md transition-colors hover:bg-muted',
                        !isEditing && 'opacity-40',
                      )}
                    >
                      {icon}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='top'>
                    <p>{label}</p>
                  </TooltipContent>
                </Tooltip>
              ))}

              <div className='h-5 w-px bg-border' />

              {/* Delete */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    aria-label='Delete sticky note'
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    className='flex size-7 items-center justify-center rounded-md transition-colors hover:text-destructive'
                  >
                    <Trash2 className='size-4' />
                  </button>
                </TooltipTrigger>
                <TooltipContent side='top'>
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        <DeleteConfirmationModal
          isOpen={isDeleteConfirmOpen}
          title='Delete sticky note'
          description='Are you sure you want to delete this sticky note? This action cannot be undone.'
          buttonLabel='Delete'
          handleDelete={handleConfirmDelete}
          handleCancel={() => setIsDeleteConfirmOpen(false)}
        />
      </>
    );
  },
);

StickyNoteToolbar.displayName = 'StickyNoteToolbar';
